import { useRef, useState, useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Wrench, DollarSign, TrendingUp, Clock, Sparkles, FileText, ExternalLink, Users } from 'lucide-react';
import { isVideoUrl, isPdfUrl } from '../lib/media';
import { responsiveImgProps } from '../lib/responsiveImage';
import { displayMoney } from '../lib/money';
import { buildRoiSections, hasAnyRoi, ROI_SECTIONS } from '../lib/roi';

const COLOR_MAP = {
  green: '#22C55E',
  yellow: '#EAB308',
  blue: '#3B82F6',
};

const COMPLETION_SIGNS = {
  planned: '?',
  'in-progress': '~',
  completed: '\u2713',
};

const ROI_ICON_MAP = {
  wrench: Wrench,
  dollar: DollarSign,
  trending: TrendingUp,
  clock: Clock,
  sparkles: Sparkles,
};

// Single combined popup. Layout: [BEFORE | AFTER] side-by-side with ROI below.
// AFTER image is rendered slightly LARGER than BEFORE (≈20% wider) per user spec.
const POPUP_WIDTH_BOTH = 320; // both images side by side (incl. ROI when present)
const POPUP_WIDTH_ONE  = 180; // just one image (or one image + ROI)
const VIEWPORT_MARGIN = 8;
const GAP = 10;

export default function MapHotspot({ area, onClick, editMode, onPositionChange, usersByEmail = {}, openSnags = 0 }) {
  const color = COLOR_MAP[area.color] || COLOR_MAP[area.status] || '#22C55E';
  const sign = COMPLETION_SIGNS[area.completion] || COMPLETION_SIGNS[area.status] || '?';
  const [dragging, setDragging] = useState(false);
  const [showRender, setShowRender] = useState(false);
  const [anchor, setAnchor] = useState(null); // { cx, top, bottom }
  const dragRef = useRef(null);
  const popupRef = useRef(null);
  const [popupPos, setPopupPos] = useState(null);
  // iter-111 — Keep popup alive while the user is hovering the popup
  // itself (so they can scroll the description / ROI). A tiny grace
  // timer lets the cursor move from the hotspot to the popup without
  // the widget snapping shut.
  const hoverInsideRef = useRef(false);
  const closeTimerRef = useRef(null);
  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      if (!hoverInsideRef.current) setShowRender(false);
    }, 140);
  };
  useEffect(() => () => cancelClose(), []);
  // iter-110 — Profile picture priority per user spec:
  //   renders[0] → after_image → before_image → images[0].
  // Each "slot" in the popup falls back through the chain so a widget
  // always renders SOMETHING when any picture exists for the area.
  const firstNonMedia = (arr) => (arr || []).find((r) => r && !isVideoUrl(r) && !isPdfUrl(r));
  const renderImage = firstNonMedia(area.renders);
  // AFTER slot: explicit pin (area.after_image) wins, then renders[0],
  // matching the BEFORE chain shape just below. Iter-111D fix — without
  // this, pinning a render as AFTER had no effect unless the admin
  // also dragged that render to position 0.
  const afterImage = area.after_image || renderImage || null;
  // BEFORE slot prefers the curated before_image, then images[0] as a
  // last-resort fallback so an empty area never shows a dead widget.
  const beforeImage = area.before_image || firstNonMedia(area.images) || null;
  const firstRenderImage = afterImage;
  const hasRenders = !editMode && !!afterImage;
  const hasBeforeImage = !editMode && !!beforeImage && beforeImage !== afterImage;
  const hasRoi = !editMode && hasAnyRoi(area);
  const roiSections = useMemo(() => (hasRoi ? buildRoiSections(area) : null), [area, hasRoi]);
  const activeRoiSections = useMemo(() => {
    if (!roiSections) return [];
    return ROI_SECTIONS.filter((s) => (roiSections[s.key] || '').trim());
  }, [roiSections]);
  const roiOverview = roiSections ? roiSections.overview : '';
  const documents = !editMode ? (area.documents || []).filter(Boolean) : [];
  const hasDocuments = documents.length > 0;
  const responsibleUsers = !editMode
    ? (area.responsible_user_emails || [])
        .map((email) => usersByEmail[email] || { email, name: email })
        .filter(Boolean)
    : [];
  const hasResponsibleUsers = responsibleUsers.length > 0;
  const hasHoverContent = hasRenders || hasBeforeImage || hasRoi || hasDocuments || hasResponsibleUsers;

  const measureAnchor = useCallback(() => {
    if (!dragRef.current) return;
    const r = dragRef.current.getBoundingClientRect();
    setAnchor({ cx: r.left + r.width / 2, top: r.top, bottom: r.bottom });
  }, []);

  // Re-measure popup position on scroll/resize while visible
  useEffect(() => {
    if (!showRender) return;
    const onUpdate = () => measureAnchor();
    window.addEventListener('scroll', onUpdate, true);
    window.addEventListener('resize', onUpdate);
    return () => {
      window.removeEventListener('scroll', onUpdate, true);
      window.removeEventListener('resize', onUpdate);
    };
  }, [showRender, measureAnchor]);

  const [roiMaxHeight, setRoiMaxHeight] = useState(null);
  const [popupWidth, setPopupWidth] = useState(POPUP_WIDTH_BOTH);

  // Single combined popup. Prefers placement above the widget; flips below if not
  // enough vertical room. Width adapts to whether one or both images are shown.
  useLayoutEffect(() => {
    if (!showRender || !anchor) {
      setPopupPos(null);
      return;
    }
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const PROGRESS_OFFSET = 30;

    const desiredW = (hasRenders && hasBeforeImage) ? POPUP_WIDTH_BOTH : POPUP_WIDTH_ONE;
    const effectiveW = Math.min(hasRoi ? POPUP_WIDTH_BOTH : desiredW, vw - VIEWPORT_MARGIN * 2);
    setPopupWidth(effectiveW);

    if (popupRef.current) {
      const halfW = effectiveW / 2;
      let left = anchor.cx - halfW;
      left = Math.max(VIEWPORT_MARGIN, Math.min(vw - effectiveW - VIEWPORT_MARGIN, left));
      const h = popupRef.current.offsetHeight;
      let top = anchor.top - GAP - h;
      let spaceForRoi = anchor.top - GAP - VIEWPORT_MARGIN - 130; // reserve for image row + name pill
      if (top < VIEWPORT_MARGIN) {
        top = Math.min(vh - h - VIEWPORT_MARGIN, anchor.bottom + GAP + PROGRESS_OFFSET);
        spaceForRoi = vh - anchor.bottom - GAP - PROGRESS_OFFSET - VIEWPORT_MARGIN - 130;
      }
      setRoiMaxHeight(Math.max(80, spaceForRoi));
      setPopupPos({ left, top });
    }
  }, [showRender, anchor, hasRenders, hasBeforeImage, hasRoi]);

  const handleMouseDown = useCallback((e) => {
    if (!editMode) return;
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);

    const parent = e.currentTarget.parentElement;
    const rect = parent.getBoundingClientRect();

    const onMove = (ev) => {
      const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const clientY = ev.touches ? ev.touches[0].clientY : ev.clientY;
      const x = ((clientX - rect.left) / rect.width) * 100;
      const y = ((clientY - rect.top) / rect.height) * 100;
      const clampedX = Math.max(0, Math.min(100, x));
      const clampedY = Math.max(0, Math.min(100, y));
      if (dragRef.current) {
        dragRef.current.style.left = `${clampedX}%`;
        dragRef.current.style.top = `${clampedY}%`;
      }
    };

    const onUp = (ev) => {
      const clientX = ev.changedTouches ? ev.changedTouches[0].clientX : ev.clientX;
      const clientY = ev.changedTouches ? ev.changedTouches[0].clientY : ev.clientY;
      const x = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
      const y = Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100));
      setDragging(false);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onUp);
      if (onPositionChange) {
        onPositionChange(area.id, Math.round(x * 10) / 10, Math.round(y * 10) / 10);
      }
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onUp);
  }, [editMode, area.id, onPositionChange]);

  const handleClick = (e) => {
    if (editMode) {
      e.stopPropagation();
      return;
    }
    onClick(area);
  };

  const handleMouseEnter = () => {
    if (!hasHoverContent) return;
    cancelClose();
    measureAnchor();
    setShowRender(true);
  };

  const handleMouseLeave = () => {
    scheduleClose();
  };

  const popups = showRender && typeof document !== 'undefined' ? createPortal(
    <div
      ref={popupRef}
      className="fixed"
      onMouseEnter={() => { hoverInsideRef.current = true; cancelClose(); }}
      onMouseLeave={() => { hoverInsideRef.current = false; scheduleClose(); }}
      style={{
        left: popupPos ? popupPos.left : -9999,
        top: popupPos ? popupPos.top : -9999,
        width: popupWidth,
        zIndex: 9999,
        opacity: popupPos ? 1 : 0,
        transition: 'opacity 120ms ease-out',
        pointerEvents: 'auto',
      }}
    >
      <div
        className="rounded-xl overflow-hidden border border-white/90 bg-white"
        style={{
          boxShadow: hasRoi
            ? '0 0 0 1px rgba(212, 175, 55, 0.45), 0 12px 36px rgba(10, 26, 47, 0.38), 0 0 28px rgba(212, 175, 55, 0.22)'
            : '0 0 0 1px rgba(0, 61, 165, 0.35), 0 10px 30px rgba(0,0,0,0.35), 0 0 24px rgba(0, 61, 165, 0.22)',
          animation: 'hyatt-card-enter 380ms cubic-bezier(0.2, 0.9, 0.3, 1.2) both',
        }}
      >
        {/* Top row: BEFORE + AFTER side-by-side. AFTER is rendered slightly LARGER than BEFORE via increased flex weight. */}
        {(hasBeforeImage || hasRenders) && (
          <div className="flex" style={{ backgroundColor: '#F8FAF9' }}>
            {hasBeforeImage && (
              <div className="flex flex-col" style={{ flex: hasRenders ? '1' : '1', borderRight: hasRenders ? '1px solid rgba(0,0,0,0.06)' : 'none' }}>
                <div className="py-1 text-center" style={{ backgroundColor: '#EF4444' }}>
                  <span className="text-[9px] font-bold text-white tracking-wider" style={{ fontFamily: 'Manrope' }}>BEFORE</span>
                </div>
                <img {...responsiveImgProps(beforeImage, '180px')} alt={`${area.name} before`} className="w-full h-auto block object-cover" style={{ aspectRatio: '4/3' }} />
              </div>
            )}
            {hasRenders && (
              <div className="flex flex-col" style={{ flex: hasBeforeImage ? '1.2' : '1' }}>
                <div className="py-1 text-center" style={{ backgroundColor: '#22C55E' }}>
                  <span className="text-[9px] font-bold text-white tracking-wider" style={{ fontFamily: 'Manrope' }}>AFTER</span>
                </div>
                <img {...responsiveImgProps(firstRenderImage, '200px')} alt={`${area.name} after`} className="w-full h-auto block object-cover" style={{ aspectRatio: '4/3' }} />
              </div>
            )}
          </div>
        )}
        {/* ROI section — Hyatt Executive card style */}
        {hasRoi && (
          <div className="relative">
            {/* Gold accent stripe at the very top of the ROI section */}
            <div
              style={{
                height: '3px',
                width: '100%',
                background: 'linear-gradient(90deg, #B8860B 0%, #F4E4BC 20%, #D4AF37 50%, #F4E4BC 80%, #B8860B 100%)',
                backgroundSize: '200% 100%',
                animation: 'hyatt-gold-shimmer 3.5s linear infinite',
              }}
            />
            {/* Royal Hyatt-blue gradient header */}
            <div
              className="px-3.5 pt-2.5 pb-2"
              style={{
                background: 'linear-gradient(135deg, #003DA5 0%, #1F4FBE 55%, #003DA5 100%)',
              }}
            >
              <div className="flex items-center gap-2">
                <span
                  aria-hidden
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle at 30% 30%, #F4E4BC 0%, #D4AF37 55%, #B8860B 100%)',
                    color: '#0A1A2F',
                    fontSize: '10px',
                    fontWeight: 900,
                    fontFamily: 'Georgia, "Times New Roman", serif',
                    boxShadow: '0 0 0 2px rgba(212, 175, 55, 0.4), 0 2px 6px rgba(184, 134, 11, 0.45), inset 0 1px 2px rgba(255, 255, 255, 0.55)',
                    flexShrink: 0,
                  }}
                >
                  $
                </span>
                <div className="flex-1 min-w-0">
                  <div
                    className="text-[10px] font-bold tracking-[0.18em] uppercase"
                    style={{
                      color: '#F4E4BC',
                      fontFamily: 'Georgia, "Times New Roman", serif',
                      letterSpacing: '0.18em',
                      textShadow: '0 1px 2px rgba(0,0,0,0.25)',
                    }}
                  >
                    Proposed Enhancements
                  </div>
                  <div
                    className="text-[8px] font-semibold tracking-[0.25em] uppercase"
                    style={{ color: 'rgba(244, 228, 188, 0.75)', fontFamily: 'Manrope, sans-serif' }}
                  >
                    &amp; Return on Investment
                  </div>
                </div>
              </div>
            </div>
            {/* White body with subtle diamond pattern background + gold seal */}
            <div
              className="hyatt-diamond-bg px-3.5 pt-3 pb-3 relative"
              style={{ borderTop: '1px solid rgba(212, 175, 55, 0.35)' }}
            >
              {/* Floating gold "ROI" wax seal — sits just under the blue header, right side */}
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  top: '-14px',
                  right: '12px',
                  width: '38px',
                  height: '38px',
                  borderRadius: '50%',
                  background: 'radial-gradient(circle at 32% 28%, #F9E8B0 0%, #D4AF37 45%, #A77C0A 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#0A1A2F',
                  fontSize: '9px',
                  fontWeight: 900,
                  fontFamily: 'Georgia, "Times New Roman", serif',
                  letterSpacing: '0.05em',
                  border: '1.5px solid rgba(255, 255, 255, 0.6)',
                  animation: 'hyatt-seal-pulse 2.8s ease-in-out infinite',
                  textShadow: '0 1px 1px rgba(255,255,255,0.35)',
                  transform: 'rotate(-6deg)',
                }}
              >
                ROI
              </div>
              <div
                style={{
                  maxHeight: roiMaxHeight || 220,
                  overflowY: 'auto',
                  pointerEvents: 'auto',
                  paddingRight: '44px', // leave space for the seal
                }}
              >
                {/* Overview blurb — shown only if free text exists without labeled sections or alongside sections */}
                {roiOverview && (
                  <div
                    className="text-[11px] leading-relaxed whitespace-pre-wrap"
                    style={{
                      color: '#1F2937',
                      fontFamily: 'Manrope, sans-serif',
                      marginBottom: activeRoiSections.length > 0 ? '10px' : 0,
                      paddingBottom: activeRoiSections.length > 0 ? '10px' : 0,
                      borderBottom: activeRoiSections.length > 0 ? '1px solid rgba(212, 175, 55, 0.35)' : 'none',
                    }}
                  >
                    {roiOverview}
                  </div>
                )}
                {/* Structured sections */}
                {activeRoiSections.map((sec, idx) => {
                  const Icon = ROI_ICON_MAP[sec.icon];
                  const rawValue = (roiSections[sec.key] || '').trim();
                  // iter-111G — Auto-prefix "$" on Investment / Return
                  // values that don't already carry a currency symbol.
                  // Other sections (Scope, Timeline, Benefits) keep
                  // their raw text untouched.
                  const value = (sec.key === 'investment' || sec.key === 'expected_return')
                    ? displayMoney(rawValue)
                    : rawValue;
                  const isLast = idx === activeRoiSections.length - 1;
                  return (
                    <div
                      key={sec.key}
                      className="flex items-start gap-2.5"
                      style={{
                        padding: '8px 0',
                        borderBottom: isLast ? 'none' : '1px solid rgba(212, 175, 55, 0.28)',
                      }}
                    >
                      <span
                        aria-hidden
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '22px',
                          height: '22px',
                          borderRadius: '50%',
                          background: 'radial-gradient(circle at 30% 30%, #F4E4BC 0%, #D4AF37 60%, #B8860B 100%)',
                          color: sec.color,
                          flexShrink: 0,
                          marginTop: '1px',
                          boxShadow: '0 1px 3px rgba(184, 134, 11, 0.35), inset 0 1px 1px rgba(255,255,255,0.55)',
                        }}
                      >
                        {Icon ? <Icon size={12} strokeWidth={2.5} /> : null}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div
                          className="text-[9px] font-bold tracking-[0.14em] uppercase"
                          style={{
                            color: sec.color,
                            fontFamily: 'Georgia, "Times New Roman", serif',
                            marginBottom: '2px',
                          }}
                        >
                          {sec.label}
                        </div>
                        <div
                          className="text-[11px] leading-snug whitespace-pre-wrap"
                          style={{ color: '#1F2937', fontFamily: 'Manrope, sans-serif' }}
                        >
                          {value}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Documents — generic file attachments. Shown beneath ROI / images
            with a small purple header strip and one chip per file. Filename
            comes from the URL tail; clicking opens in a new tab. */}
        {hasDocuments && (
          <div className="relative" style={{ borderTop: '1px solid rgba(124, 58, 237, 0.25)' }}>
            <div
              className="px-3 py-1.5 flex items-center gap-1.5"
              style={{ backgroundColor: 'rgba(124, 58, 237, 0.08)' }}
            >
              <FileText className="w-3 h-3" style={{ color: '#7C3AED' }} aria-hidden />
              <span
                className="text-[9px] font-bold tracking-[0.16em] uppercase"
                style={{ color: '#5B21B6', fontFamily: 'Georgia, "Times New Roman", serif' }}
              >
                Documents · {documents.length}
              </span>
            </div>
            <div className="px-3 py-2 max-h-[160px] overflow-y-auto" style={{ backgroundColor: '#FFFFFF', pointerEvents: 'auto' }}>
              <ul className="space-y-1">
                {documents.map((url) => {
                  const filename = decodeURIComponent((url.split('/').pop() || 'document').split('?')[0]);
                  return (
                    <li key={url}>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-2 py-1.5 rounded-lg transition-colors"
                        style={{ backgroundColor: '#F8FAFC' }}
                        data-testid={`doc-link-${area.id}`}
                      >
                        <FileText className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#7C3AED' }} aria-hidden />
                        <span
                          className="text-[11px] truncate flex-1"
                          style={{ color: '#1F2937', fontFamily: 'Manrope, sans-serif' }}
                        >
                          {filename}
                        </span>
                        <ExternalLink className="w-3 h-3 flex-shrink-0" style={{ color: '#7C3AED', opacity: 0.7 }} aria-hidden />
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}

        {/* Users responsible for the project — owner avatars row */}
        {hasResponsibleUsers && (
          <div className="relative" style={{ borderTop: '1px solid rgba(31, 79, 190, 0.25)' }}>
            <div
              className="px-3 py-1.5 flex items-center gap-1.5"
              style={{ backgroundColor: 'rgba(31, 79, 190, 0.08)' }}
            >
              <Users className="w-3 h-3" style={{ color: '#1F4FBE' }} aria-hidden />
              <span
                className="text-[9px] font-bold tracking-[0.16em] uppercase"
                style={{ color: '#003DA5', fontFamily: 'Georgia, "Times New Roman", serif' }}
              >
                Users Responsible · {responsibleUsers.length}
              </span>
            </div>
            <div className="px-3 py-2" style={{ backgroundColor: '#FFFFFF' }}>
              <ul className="flex flex-wrap gap-1.5">
                {responsibleUsers.map((u) => {
                  const initials = (u.name || u.email || '?').split(/[ @]+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase();
                  return (
                    <li
                      key={u.email}
                      className="flex items-center gap-1.5 pl-0.5 pr-2 py-0.5 rounded-full"
                      style={{ backgroundColor: '#F8FAFC', border: '1px solid #E5E7EB' }}
                      title={`${u.name || u.email}${u.title ? ' · ' + u.title : ''}`}
                    >
                      <span
                        className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center text-[8px] font-bold"
                        style={{ backgroundColor: '#E0E7FF', color: '#1F4FBE' }}
                      >
                        {u.photo_url
                          ? <img src={u.photo_url} alt="" className="w-full h-full object-cover" />
                          : initials}
                      </span>
                      <span
                        className="text-[10px] font-medium truncate max-w-[120px]"
                        style={{ color: '#1F2937', fontFamily: 'Manrope, sans-serif' }}
                      >
                        {u.name || u.email}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}
      </div>
      <div className="flex justify-center mt-1">
        <span className="px-3 py-0.5 rounded-full text-[10px] font-bold shadow-md" style={{ backgroundColor: 'white', color: '#1A2E2E', fontFamily: 'Manrope' }}>
          {area.name}
        </span>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div
      ref={dragRef}
      data-testid={`hotspot-${area.id}`}
      className="hotspot-btn hotspot-responsive group"
      style={{
        left: `${area.position_x}%`,
        top: `${area.position_y}%`,
        transform: 'translate(-50%, -50%)',
        cursor: editMode ? 'grab' : 'pointer',
        zIndex: dragging ? 50 : showRender ? 40 : 10,
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleMouseDown}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {popups}

      {/* Glow ring */}
      {!editMode && (
        <span className="hotspot-ping absolute inset-0 rounded-full" style={{ backgroundColor: `${color}40` }} />
      )}

      {/* Inner dot with completion sign */}
      <span
        className="hotspot-glow relative flex items-center justify-center w-8 h-8 rounded-full shadow-lg border-2 transition-transform duration-200"
        style={{
          backgroundColor: color,
          borderColor: editMode ? '#FFD700' : 'rgba(255,255,255,0.8)',
          borderWidth: editMode ? '3px' : '2px',
          boxShadow: `0 0 12px ${color}80, 0 0 24px ${color}40`,
        }}
      >
        <span className="text-white font-bold text-xs" style={{ fontFamily: 'Manrope, sans-serif', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
          {sign}
        </span>
      </span>

      {/* ROI indicator chip — visible only when enhancements_roi exists (public view) */}
      {hasRoi && (
        <span
          aria-label="Has proposed enhancements & ROI"
          className="roi-chip"
          style={{
            position: 'absolute',
            top: '-6px',
            right: '-8px',
            width: '14px',
            height: '14px',
            borderRadius: '50%',
            backgroundColor: '#14B8A6',
            border: '2px solid #FFFFFF',
            boxShadow: '0 0 0 1px rgba(20,184,166,0.35), 0 0 8px rgba(20,184,166,0.85), 0 0 14px rgba(20,184,166,0.5)',
            zIndex: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            animation: 'roi-chip-pulse 2.2s ease-in-out infinite',
          }}
        >
          <span
            style={{
              color: '#FFFFFF',
              fontSize: '8px',
              fontWeight: 900,
              lineHeight: 1,
              fontFamily: 'Manrope, sans-serif',
              letterSpacing: '-0.02em',
            }}
          >
            $
          </span>
        </span>
      )}

      {/* Open-snags badge — small red counter at top-left of the marker.
          Shown (public/non-edit view) whenever the area has one or more
          outstanding snags, so owners can glance which areas still carry
          defects. Non-interactive: the hotspot click still opens the area. */}
      {!editMode && openSnags > 0 && (
        <span
          data-testid={`snag-badge-${area.id}`}
          aria-label={`${openSnags} open snag${openSnags === 1 ? '' : 's'}`}
          title={`${openSnags} open snag${openSnags === 1 ? '' : 's'}`}
          style={{
            position: 'absolute',
            top: '-7px',
            left: '-9px',
            minWidth: '16px',
            height: '16px',
            padding: '0 3px',
            borderRadius: '8px',
            backgroundColor: '#EF4444',
            border: '2px solid #FFFFFF',
            boxShadow: '0 0 0 1px rgba(239,68,68,0.35), 0 0 8px rgba(239,68,68,0.85), 0 0 14px rgba(239,68,68,0.5)',
            zIndex: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            animation: 'roi-chip-pulse 2.2s ease-in-out infinite',
          }}
        >
          <span
            style={{
              color: '#FFFFFF',
              fontSize: '9px',
              fontWeight: 900,
              lineHeight: 1,
              fontFamily: 'Manrope, sans-serif',
              letterSpacing: '-0.02em',
            }}
          >
            {openSnags > 99 ? '99+' : openSnags}
          </span>
        </span>
      )}

      {/* Tooltip */}
      <span className={editMode ? 'hotspot-tooltip opacity-100' : 'hotspot-tooltip'}>
        <span
          className="map-label px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap shadow-md"
          style={{ color: '#003DA5', fontFamily: 'Manrope, sans-serif' }}
        >
          {area.name}
          {editMode && (
            <span className="ml-1 text-[10px] font-normal" style={{ color: '#7A8C8C' }}>
              ({Math.round(area.position_x)}%, {Math.round(area.position_y)}%)
            </span>
          )}
        </span>
      </span>

      {/* Progress bar + percentage */}
      {!editMode && (
        <div className="absolute top-full mt-1.5 left-1/2 -translate-x-1/2 flex flex-col items-center" style={{ minWidth: '40px' }}>
          <div style={{ width: '36px', height: '5px', backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '3px', overflow: 'hidden' }}>
            <div className="hotspot-progress-bar" style={{ width: `${Math.min(100, area.progress_percent || 0)}%`, height: '100%', backgroundColor: '#EF4444', borderRadius: '3px' }} />
          </div>
          <span className="text-[8px] font-bold mt-0.5" style={{ color: '#1A1A1A', fontFamily: 'Manrope', textShadow: '0 0 3px rgba(255,255,255,0.9)' }}>
            {area.progress_percent || 0}%
          </span>
        </div>
      )}
    </div>
  );
}
